import log from '@vcclive/log';
import { Action, SagaApi, SagaMap } from '../../interfaces/redux';
import com from '../com';
import WebChat, { PluginEvent } from '../common';
import { RootState } from './client-reducer';
import { ClientEvents } from './Events';
import { FrameManager } from './FrameManager';

const debug = log.getDebug('client-saga');

export function rootSaga(api: SagaApi<RootState>): SagaMap<RootState> | void {
    const params = new URLSearchParams(window.location.search);
    const projectId = +(params.get('p') ?? 0);
    const css = params.get('css');
    const customer = process.env.CUSTOMER || window.location.hostname.replace(/^(.+?)\..*$/, '$1');
    const userAgent = navigator.userAgent;
    const lang = navigator.language;
    const events = new ClientEvents(api);
    const frameManager = new FrameManager(api);

    if (css) {
        injectCSS(css);
    }

    log.debug(window.location.search, {
        projectId,
        customer,
    });

    com.init({
        projectId,
        customer,
        app: 'webchat-plugin',
        hostname: process.env.CUSTOMER
            ? `${process.env.CUSTOMER}.asp.virtual-call-center.eu`
            : window.location.hostname,
    });
    com.onConnect(() => {
        debug('connect');
        api.dispatch({ type: 'startPlugin' });
    });
    com.onDisconnect(() => {
        api.dispatch({ type: 'disconnectPlugin' });
    });

    return {
        startPlugin() {
            com.subscribe('{USER}', events.handler);
            com.subscribe('{APP}', events.handler);
            com.subscribe(`webchat-plugin/${customer}/queueinfo/${projectId}`, events.handler);
            com.subscribe('stork/disconnect/webchat-server', serverDisconnected);
            publishPluginConnected();
        },
        closeConversation() {
            cleanUpAfterSession();
        },
        closed() {
            frameManager.onClosed();
        },
        connectToServer() {
            publishPluginConnected();
        },
        initWebchat(action: Action) {
            const { settings } = action.payload;
            frameManager.init(settings);
        },
        open() {
            frameManager.onOpen();
        },
        receiveMessage(action: Action) {
            if (action.payload.from !== WebChat.FROM_SYSTEM) {
                frameManager.openChat();
            }
        },
        sendMessage(action: Action) {
            const message = action.payload;
            const state = api.getState().root;
            if (!state.queueId) {
                log.warn('User should be able send message without queueId');
                return;
            }
            const messageObj: Omit<ChatMessage, 'date'> = {
                id: message.id,
                to: com.getChannelName(),
                from: com.getChannelName(),
                message: message.message,
                pageUrl: state.pageUrl || '',
                pageTitle: state.pageTitle || '',
            };
            const payload: BaseMessagePayload = {
                contactInfo: {
                    platform: 'webchat',
                    name: '',
                    phone: '',
                    username: '',
                },
                customer,
                projectId,
                userAgent,
                lang,
                queueId: state.queueId,
                message: messageObj,
                remoteIp: com.getRemoteIp(),
            };
            publish(PluginEvent.messageSendingStarted, payload);
        },
        startNewSession() {
            log.info('relogin');
            com.relogin();
        },
    };

    function publishPluginConnected() {
        const payload: PluginConnected = {
            customer,
            projectId,
            userAgent,
            lang,
            pageUrl: api.getState().root.pageUrl || '',
            remoteIp: com.getRemoteIp(),
        };
        publish(PluginEvent.pluginConnected, payload);
    }

    function serverDisconnected() {
        api.dispatch({ type: 'serverExit' });
    }
}

function cleanUpAfterSession() {
    com.cleanClientId();
}

function publish(type: PluginEvent, payload?: any) {
    const action: Action = { type };
    if (payload) {
        action.payload = payload;
    }
    com.publish('webchat-server', action);
}

function injectCSS(css: string) {
    const element = document.createElement('link');
    element.setAttribute('rel', 'stylesheet');
    element.setAttribute('type', 'text/css');
    element.setAttribute('href', css);
    document.head.appendChild(element);
}
